<template>
  <div class="photos-hover-card-wrapper">
    <div
      class="hover-trigger"
      @mouseenter="showCard"
      @mouseleave="hideCard"
      ref="trigger"
    >
      <slot></slot>
    </div>
    <transition name="card-fade">
      <div
        v-if="isVisible && photos.length > 0"
        class="hover-card"
        :style="cardStyle"
        @mouseenter="showCard"
        @mouseleave="hideCard"
      >
        <div class="hover-card-inner">
          <div class="photos-grid">
            <div
              v-for="(photo, index) in photos"
              :key="photo.id || index"
              class="photo-item"
              :class="{ deleted: photo.deletedAt }"
            >
              <img
                :src="photo.url"
                :alt="`Photo ${index + 1}`"
                :style="{ opacity: photo.deletedAt ? 0.5 : 1 }"
                @error="handleImageError($event)"
              />
              <div class="photo-position">{{ index + 1 }}</div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "PhotosHoverCard",

  props: {
    photos: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isVisible: false,
      cardPosition: {
        top: 0,
        left: 0,
      },
      hideTimeout: null,
    };
  },

  computed: {
    cardStyle() {
      return {
        top: `${this.cardPosition.top}px`,
        left: `${this.cardPosition.left}px`,
      };
    },
  },

  methods: {
    calculatePosition() {
      const trigger = this.$refs.trigger;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const cardWidth = 320;
      const cardHeight = 400;
      const margin = 12;

      let left = rect.right + margin;
      let top = rect.top;

      // Ajustement horizontal : si déborde à droite, afficher à gauche
      if (left + cardWidth > window.innerWidth - margin) {
        left = rect.left - cardWidth - margin;
      }

      // Ajustement vertical : si déborde en bas
      if (top + cardHeight > window.innerHeight - margin) {
        top = window.innerHeight - cardHeight - margin;
      }

      // Si déborde en haut
      if (top < margin) {
        top = margin;
      }

      this.cardPosition = { top, left };
    },

    showCard() {
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }
      this.isVisible = true;
      this.$nextTick(() => {
        this.calculatePosition();
      });
    },

    hideCard() {
      this.hideTimeout = setTimeout(() => {
        this.isVisible = false;
      }, 100);
    },

    handleImageError(event) {
      event.target.style.display = "none";
    },
  },

  beforeDestroy() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
  },
};
</script>

<style scoped>
.photos-hover-card-wrapper {
  position: relative;
  display: inline-block;
}

.hover-trigger {
  cursor: pointer;
}

.hover-card {
  position: fixed;
  z-index: 1000;
  width: 320px;
  max-height: 400px;
  overflow-y: auto;
}

.hover-card-inner {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  border: 1px solid #e8e8e8;
  padding: 12px;
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.photo-item {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f5f5;
  aspect-ratio: 2/3;
}

.photo-item::after {
  content: "";
  display: block;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 5;
  mix-blend-mode: multiply;
  position: absolute;
  background: radial-gradient(circle, #fff, #f5f3f1);
  pointer-events: none;
}

.photo-item.deleted::before {
  content: "";
  position: absolute;
  top: 50%;
  left: -42%;
  width: 188%;
  height: 1px;
  background: #e1e1e1;
  transform: rotate(56deg);
  z-index: 10;
  pointer-events: none;
}

.photo-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease;
}

.photo-position {
  position: absolute;
  bottom: 4px;
  left: 4px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  z-index: 15;
}

/* Scrollbar styling */
.hover-card::-webkit-scrollbar {
  width: 4px;
}

.hover-card::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 2px;
}

.hover-card::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 2px;
}

/* Transition */
.card-fade-enter-active,
.card-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.card-fade-enter-from,
.card-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
